import {predicate} from "./MutualParams";


export {default as withSequence} from "./withSequence";
export {default as interpolate} from "./interpolate";
export {default as groupBy} from "./groupBy";
export {default as removeItem} from "./removeItem";
export * from "./fadeInOut";
export {default as sanitize} from "./sanitize";
export {default as map} from "./map";
export {default as FooMap} from "./FooMap";
export {default as sendRequest} from "./sendRequest";

export const hexColorToRGBA={
    id:"hexColorToRGBA",
    type:"(hexcolor:string,asarray:boolean):String|Number[]",
    description:"converts an hexadecimal color format to RGBA format",
    params:[
        {
            name:"hexcolor",
            type:"string",
        },
        {
            name:"asarray",
            type:"boolean",
            description:"if true the color rgba representation is returned an an array of decimals [r,g,b,a] @default false",
        },
    ],
    returns:{
        name:"rgbaColor",
        type:"String|Number[]",
    },
},hexToDecimal={
    id:"hexToDecimal",
    type:"(hexcode:string):Number",
    description:"converts an hexadecimal to decimal",
    params:[
        {
            name:"hexcode",
            type:"string",
            description:"hexadecimal to convert",
        },
    ],
    returns:{
        name:"decimal",
        type:"number",
    },
},getAdjacentDate={
    id:"getAdjacentDate",
    description:"Returns an adjacent date",
    overloads:[
        {type:"(date:string,offset:number):String"},
        {
            type:"(date:string,format:string,offset:number):String",
            props:[
                {
                    name:"date",
                    type:"string",
                    description:`
                        supported delimiters: 
                        @1)"/" @2)"-" @3)" " @4)","
                    `,
                },
                {
                    name:"format",
                    type:`"dmy"|"mdy"|"ymd"`,
                    description:`default to dmy`
                },
                {
                    name:"offset",
                    description:`
                        1 will return tomorrow's date
                        @-1 will return yesterday's date
                        @default to 1
                    `,
                },
            ],
        },
    ],
},getCharsInBetween={
    id:"getCharsInBetween",
    type:"(startChar:String,endChar:String,from:String):String",
    description:"Extracts all characters between the two limiters specified. @Limiters are not included",
    params:[
        {
            name:"startChar",
            type:"String",
            description:"Left limiter character @Pass empty string as parameter to include the first character",
        },
        {
            name:"endChar",
            type:"String",
            description:"Right limiter character @Pass empty string as parameter to include the last character",
        },
        {
            name:"from",
            type:"String",
            description:"string to extract from",
        },
    ],
},factorial={
    id:"factorial",
    type:"(n:Number):Number",
    returns:{
        name:"result",
        type:"number",
        description:"the factorial of n",
    },
},findItem={
    id:"findItem",
    typedynamic:true,
    type:"function",
    description:`
        a function that returns an item and its index from an array in one go, 
        instead of calling the find method then using indexOf
    `,
    params:[
        {name:"array",type:"Type[]"},
        predicate,
        {
            name:"descending",
            type:"boolean",
            description:"specifies search order, default to false",
        },
    ],
    returns:{
        name:"item",
        type:"object|null",
        description:"returns null if no such item is found",
        props:[
            {
                name:"value",
                type:"Type",
                description:"actual array item",
            },
            {
                name:"index",
                type:"number",
                description:"item index",
            },
        ],
    },
},isEmail={
    id:"isEmail",
    type:"(str:String):Boolean",
    description:"returns true if the string is a valid email",
},capitalize={
    id:"capitalize",
    type:"(str:String,count:Number):String",
    params:[
        {
            name:"str",
            type:"String",
            description:"string to capitalize its words",
        },
        {
            name:"count",
            type:"number",
            description:`
                number of words to capitalize
                @if count is 2, then only the first two words are capitalized
                @if count is 0, then all words are capitalized
                @default to 0
            `,
        },
    ],
},randomColor={
    id:"randomColor",
    type:"(andFrom?:String[]):String",
    description:"returns a random color",
    params:[{
        name:"andFrom",
        type:"String[]",
        description:"array of colors to add to possible values",
    }],
    returns:{
        name:"color",
        type:"string",
        description:`
            a hsl( * , 100% , 50% ) color in rgb format 
            or a color from the <span>andFrom</span> array if specified
        `,
    },
},randomId={
    id:"randomId",
    type:"(prefix?:string,length?:number):string",
    description:"returns a random string of latin letters and integers",
},getDays={
    id:"getDays",
    type:"(start?:String):String[]",
    description:"returns an array containing the 7 days of the week with start-param value as first day",
    params:[
        {
            name:"start",
            type:`"monday"|"tuesday"|"wednesday"|"thursday"|"friday"|"saturday"|"sunday"`,
            description:"first day, default to monday",
        }
    ],
},isLeapYear={
    id:"isLeapYear",
    type:"(year?:Number):Boolean",
    description:"year to check, default to current year",
},getMonths={
    id:"getMonths",
    type:{
        name:"(isLeapYear?:boolean):months",
        hash:"isLeapYear",
    },
    description:"returns an array of objects representing the months of the year,default to false",
    params:[
        {
            name:"isLeapYear",
            type:"boolean",
            description:"if true, february length value would be 29 else 28",
        },
    ],
    returns:{
        name:"Months",
        type:"{name:String,length:Number}[]",
        props:[
            {
                name:"name",
                type:"string",
                description:"month english-name in lowercase",
            },
            {
                name:"daycount",
                type:"number",
                description:"month days count",
            },
            {
                name:"ordinal",
                type:"number",
            },
        ],
    },
},getTimeDuration={
    id:"getTimeDuration",
    type:"(start:String,end:String):Number",
    description:"Returns the duration between two times in seconds",
    params:["start","end"].map((name,i,{length})=>({
        name,
        type:"string",
        description:((i+1)===length)&&"supported formats : hh:mm:ss h:m:s h:m:: h::",
    })),
},useBlobImageData={
    id:"useBlobImageData",
    type:"(blob:Blob,callback:function):void",
    description:"Extracts ImageData from Blob",
    params:[
        {name:"blob",type:"blob"},
        {
            name:"callback",
            type:"(imageData:ImageData):void",
        }
    ],
},parseJSON={
    id:"parseJSON",
    type:"(json:String):any|null",
    description:"Uses JSON.parse method to parse json parameter but returns null in case of an error",
    params:[
        {
            name:"json",type:"string",
            description:"json string to parse",
        }
    ],
},randomItem={
    id:"randomItem",
    typedynamic:true,
    type:"(array:Type[]):Type",
    description:"returns a random item from an array ",
},isTouchDevice={
    id:"isTouchDevice",
    type:"():Boolean",
    description:"Returns true if device supports touch gestures",
}
